import React, { useEffect, useState, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * BackToTopFAB (iter72) — 48px round white button with a thin progress-ring
 * that fills as the user scrolls. The ring SVG uses a viewBox (0 0 100 100)
 * so it auto-scales to whatever button size we use — fixes the iter71
 * misalignment where a fixed-pixel SVG didn't match the button bounds.
 *
 * Appears once window.scrollY > 500px, fades out at the top.
 */

const SHOW_AFTER_PX = 500;
// viewBox-based ring constants so the SVG scales perfectly with the button.
const RING_VB = 100;            // viewBox dimension
const RING_STROKE = 6;          // matches a ~3px visible stroke at 48px
const RING_RADIUS = (RING_VB - RING_STROKE) / 2;   // 47
const RING_CIRC = 2 * Math.PI * RING_RADIUS;        // ~295.3

const BackToTopFAB = () => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1

  const onScroll = useCallback(() => {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    setVisible(y > SHOW_AFTER_PX);
    const max = Math.max(
      1,
      (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0),
    );
    setProgress(Math.min(1, Math.max(0, y / max)));
  }, []);

  useEffect(() => {
    onScroll(); // initial
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const handleClick = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_e) {
      // Old browsers without smooth-scroll support
      window.scrollTo(0, 0);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to top"
      data-testid="back-to-top-fab"
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      className={`fixed bottom-3 right-3 z-40 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#1800ad] transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#1800ad]/20 ${
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}
    >
      {/* iter72 — viewBox-based SVG auto-scales to the button size and stays
          perfectly centered regardless of width/height. */}
      <svg viewBox={`0 0 ${RING_VB} ${RING_VB}`} className="absolute inset-0 w-full h-full -rotate-90" aria-hidden="true">
        <circle
          cx={RING_VB / 2}
          cy={RING_VB / 2}
          r={RING_RADIUS}
          stroke="#1800ad22"
          strokeWidth={RING_STROKE}
          fill="none"
        />
        <circle
          cx={RING_VB / 2}
          cy={RING_VB / 2}
          r={RING_RADIUS}
          stroke="#1800ad"
          strokeWidth={RING_STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={RING_CIRC}
          strokeDashoffset={RING_CIRC * (1 - progress)}
          data-testid="back-to-top-progress"
        />
      </svg>
      <ArrowUp size={18} strokeWidth={2.5} className="relative z-10" />
    </button>
  );
};

export default BackToTopFAB;
